import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface SeasonalPeriod {
  id: string
  unitType: string
  startDate: string // YYYY-MM-DD format
  endDate: string // YYYY-MM-DD format
  season: 'low' | 'mid' | 'high'
}

interface SeasonRate {
  id: string
  seasonName: string
  dateRange: string
  highSeasonRate: number
  midSeasonRate: number
  lowSeasonRate: number
  minStay: number
  adminFee: number
  breakageDeposit: number
}

interface Unit {
  id: string
  number: string
  type: string
  features: string
  status: string
  highSeasonRate: number
  midSeasonRate: number
  lowSeasonRate: number
  minStay?: number
  adminFee?: number
  breakageDeposit?: number
  images?: string[]
  heroImageIndex?: number
  seasonRates?: SeasonRate[]
}

interface RoomsState {
  units: Unit[]
  seasonalCalendar: Record<string, SeasonalPeriod[]> // unitType -> array of seasonal periods
  globalSeasons: SeasonalPeriod[] // global (resort-wide) seasonal periods
  loading: boolean
  error: string | null
}

const initialState: RoomsState = {
  units: [],
  seasonalCalendar: {},
  globalSeasons: [],
  loading: false,
  error: null,
}

const roomsSlice = createSlice({
  name: 'rooms',
  initialState,
  reducers: {
    updateUnits: (state, action: PayloadAction<Unit[]>) => {
      state.units = action.payload
    },
    addUnit: (state, action: PayloadAction<Unit>) => {
      state.units.push(action.payload)
    },
    updateUnit: (state, action: PayloadAction<Unit>) => {
      const index = state.units.findIndex(unit => unit.id === action.payload.id)
      if (index !== -1) {
        state.units[index] = action.payload
      }
    },
    deleteUnit: (state, action: PayloadAction<string>) => {
      state.units = state.units.filter(unit => unit.id !== action.payload)
    },
    updateSeasonalCalendar: (state, action: PayloadAction<{ unitType: string; period: SeasonalPeriod }>) => {
      const { unitType, period } = action.payload
      
      if (!state.seasonalCalendar[unitType]) {
        state.seasonalCalendar[unitType] = []
      }
      
      // Remove any overlapping periods
      state.seasonalCalendar[unitType] = state.seasonalCalendar[unitType].filter(p => 
        !(period.startDate <= p.endDate && period.endDate >= p.startDate)
      )
      
      // Add the new period
      state.seasonalCalendar[unitType].push(period)
      
      // Sort periods by start date
      state.seasonalCalendar[unitType].sort((a, b) => a.startDate.localeCompare(b.startDate))
    },
    removeSeasonalPeriod: (state, action: PayloadAction<{ unitType: string; periodId: string }>) => {
      const { unitType, periodId } = action.payload
      if (state.seasonalCalendar[unitType]) {
        state.seasonalCalendar[unitType] = state.seasonalCalendar[unitType].filter(p => p.id !== periodId)
      }
    },
    clearSeasonalCalendar: (state, action: PayloadAction<string>) => {
      const unitType = action.payload
      if (state.seasonalCalendar[unitType]) {
        state.seasonalCalendar[unitType] = []
      }
    },
    updateGlobalSeasons: (state, action: PayloadAction<SeasonalPeriod[]>) => {
      state.globalSeasons = action.payload
    },
  },
})

export const { 
  updateUnits, 
  addUnit, 
  updateUnit, 
  deleteUnit, 
  updateSeasonalCalendar,
  removeSeasonalPeriod,
  clearSeasonalCalendar,
  updateGlobalSeasons
} = roomsSlice.actions

// Helper function to get the current season for a date and unit type
export const getSeasonForDate = (date: string, unitType: string, seasonalCalendar: Record<string, SeasonalPeriod[]>, globalSeasons?: SeasonalPeriod[]): 'low' | 'mid' | 'high' => {
  // Check unit-specific periods first
  const unitCalendar = seasonalCalendar[unitType]
  if (unitCalendar) {
    for (const period of unitCalendar) {
      if (date >= period.startDate && date <= period.endDate) {
        return period.season
      }
    }
  }

  // Fall back to global (resort-wide) seasons
  if (globalSeasons) {
    for (const period of globalSeasons) {
      if (date >= period.startDate && date <= period.endDate) {
        return period.season
      }
    }
  }

  return 'mid' // Default to mid season
}

export default roomsSlice.reducer